import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { WalletSummaryCard } from "./wallet/WalletSummaryCard.js";
import { ReferralSection } from "./wallet/ReferralSection.js";
import { WalletHistoryList } from "./wallet/WalletHistoryList.js";

const db = getFirestore(app);

import { UserService } from "../../services/user.service.js";

// [Sub-Components]
const renderSubscriptionCard = (sub, isActive = false, price = 9900) => {
    const isPremium = sub?.status === 'active';
    const btnClass = isActive
        ? "btn-sub-upgrade bg-black text-white hover:bg-gray-800 cursor-pointer" // Active
        : "bg-gray-200 text-gray-400 cursor-not-allowed"; // Disabled

    const btnText = isActive
        ? `${window.locale.t('wallet.sub_card.upgrade_btn').replace('{price}', price.toLocaleString())} <span class="material-symbols-outlined text-[12px]">arrow_forward</span>`
        : window.locale.t('wallet.sub_card.coming_soon');

    return `
        <div class="bg-gradient-to-r ${isPremium ? 'from-purple-600 to-indigo-600 text-white' : 'from-gray-100 to-gray-200 text-gray-500'} rounded-xl p-4 shadow-sm flex justify-between items-center border border-gray-200/50">
            <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-full ${isPremium ? 'bg-white/20' : 'bg-white'} flex items-center justify-center">
                    <span class="material-symbols-outlined ${isPremium ? 'text-white' : 'text-gray-400'}">diamond</span>
                </div>
                <div>
                    <h4 class="text-xs font-bold uppercase tracking-wider opacity-80">${window.locale.t('wallet.sub_card.tier_label')}</h4>
                    <p class="font-bold text-lg">${isPremium ? window.locale.t('wallet.sub_card.tier_premium') : window.locale.t('wallet.sub_card.tier_standard')}</p>
                </div>
            </div>
            ${!isPremium ? `
                <button class="${btnClass} px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1" ${!isActive ? 'disabled' : ''}>
                    ${btnText}
                </button>
            ` : `
                <div class="text-right">
                    <p class="text-xs opacity-70">${window.locale.t('wallet.sub_card.renews_on')}</p>
                    <p class="font-mono font-bold text-sm">2026.02.01</p>
                </div>
            `}
        </div>
    `;
};

export const WalletTab = async (target, user) => {
    // 1. Initial Render (Skeleton)
    // We render the structure first, then populate with data.
    // Fetch generic user data for referral code (still use one-time fetch for static data if needed, or put inside listener if it changes)
    // For referral, let's keep one-time fetch as it rarely changes.
    const userData = (await getDoc(doc(db, "users", user.uid))).data();
    const referralCode = userData.referralCode || "Generating...";
    const referralCount = userData.stats?.referralCount || 0;
    const inviteLink = `${window.location.origin}/login?ref=${referralCode}`;

    // [DYNAMIC] Fetch referral reward amount from settings
    let referralRewardAmount = 500; // Default fallback
    try {
        const rewardSnap = await getDoc(doc(db, "settings", "activity_rewards"));
        if (rewardSnap.exists()) {
            referralRewardAmount = rewardSnap.data().referral?.amount || 500;
        }
    } catch (e) { console.warn("Failed to load referral config", e); }

    const referralComponent = ReferralSection(inviteLink, referralCount, referralRewardAmount);
    const historyList = new WalletHistoryList(user.uid);

    // [Step 1] Fetch Global Settings for Subscription State
    // Dynamic Import to avoid cycle if needed, but ContentService is safe
    const { ContentService } = await import("../../services/content.service.js");
    const globalSettings = await ContentService.getGlobalSettings();
    const subActive = !!globalSettings.subscription_active;
    const subPrice = globalSettings.subscription_price || 9900;

    target.innerHTML = `
        <div class="p-4 space-y-6 pb-24">
            <!-- 1. Real-time Assets Container -->
            <div id="wallet-assets-container" class="space-y-4 animate-fade-in">
                <!-- WalletSummaryCard will be injected here -->
                <div class="h-32 bg-gray-100 rounded-xl animate-pulse"></div>
            </div>

            <!-- 2. Subscription Status (Investor Readiness) -->
            <div id="wallet-subscription-container">
                <!-- Subscription Card injected here -->
            </div>

            <!-- 3. Referral Center -->
            ${referralComponent.html}

            <!-- 4. Charge Button (Floating or Fixed) -->
            <div class="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex justify-between items-center cursor-pointer hover:bg-indigo-100 transition-colors" id="btn-go-charge">
                <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <span class="material-symbols-outlined text-indigo-600">bolt</span>
                </div>
                    <div>
                        <h4 class="font-bold text-indigo-900">${window.locale.t('wallet.charge.title')}</h4>
                        <p class="text-xs text-indigo-600">${window.locale.t('wallet.charge.desc')}</p>
                    </div>
                </div>
                <span class="material-symbols-outlined text-indigo-400">chevron_right</span>
            </div>

            <!-- 5. Transaction History -->
            ${historyList.render()}
        </div>
    `;

    // 2. Event Binding
    referralComponent.bindEvents(target);
    await historyList.init(target);

    target.querySelector('#btn-go-charge').onclick = () => {
        // Navigate to Charging Station
        // Assuming SPA router or simple page swap. 
        // For now, let's use window.location.hash or a global router if available, 
        // or just render the component if it's a "Page".
        // Based on task, we are making StarChargePage.js. 
        // We'll dispatch a custom event or change hash.
        window.location.hash = '#/charging-station';
    };

    // 3. Start Real-time Listener
    // We attach the unsubscribe function to the target element so the router can clean it up.
    if (target._unsubscribeWallet) target._unsubscribeWallet();

    target._unsubscribeWallet = UserService.listenToMyWallet(user.uid, (data) => {
        const { stars, points, subscription } = data;

        // Update Assets Card
        const assetsContainer = target.querySelector('#wallet-assets-container');
        if (assetsContainer) {
            assetsContainer.innerHTML = WalletSummaryCard(points, stars);
        }

        // Update Subscription Card
        const subContainer = target.querySelector('#wallet-subscription-container');
        if (subContainer) {
            // [Dynamic] Pass Active State and Price from Global Settings
            subContainer.innerHTML = renderSubscriptionCard(subscription, subActive, subPrice);

            // [ARCH HOOK] Bind Service Action (Only if Active)
            if (subActive) {
                const upgradeBtn = subContainer.querySelector('.btn-sub-upgrade');
                if (upgradeBtn) {
                    upgradeBtn.onclick = async () => {
                        const { SubscriptionService } = await import("../../services/subscription.service.js");
                        SubscriptionService.requestUpgrade('premium'); // Single Line Call
                    };
                }
            }
        }
    }); // End listenToMyWallet
};
